import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getRecursos, fileUrl, thumbUrl } from "@/lib/api";
import { Download, ExternalLink, Play, Package, Search } from "lucide-react";
import ContentBloque from "./ContentBloque";

function ytId(url) {
  const m = (url || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}

function RecursoCard({ r }) {
  const [playing, setPlaying] = useState(false);
  const yt = r.tipo === "youtube" ? ytId(r.url) : null;
  const bloques = r.bloques || [];
  const hasBloques = bloques.length > 0;
  return (
    <div className="recurso-card" data-testid={`recurso-${r.id}`}>
      {r.imagen ? (
        <div className="recurso-cover" style={{ backgroundImage: `url(${thumbUrl(r.imagen, 800)})` }} data-testid={`recurso-portada-${r.id}`} />
      ) : r.tipo === "youtube" && yt ? (
        <div className="recurso-video">
          {playing ? (
            <iframe title={r.titulo} src={`https://www.youtube.com/embed/${yt}?autoplay=1`}
              style={{ width: "100%", height: "100%", border: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          ) : (
            <button className="recurso-thumb" onClick={() => setPlaying(true)} data-testid={`recurso-play-${r.id}`}
              style={{ backgroundImage: `url(https://img.youtube.com/vi/${yt}/hqdefault.jpg)` }}>
              <span className="recurso-playbtn"><Play size={22} /></span>
            </button>
          )}
        </div>
      ) : (
        <div className="recurso-cover recurso-cover-ph" data-testid={`recurso-portada-ph-${r.id}`}>
          <Package size={30} />
          <span>{r.categoria || "Recurso"}</span>
        </div>
      )}
      <div className="recurso-body">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span className="recurso-cat">{r.categoria}</span>
          {r.solo_vip && <span className="chip-vip" data-testid={`recurso-vip-${r.id}`}>VIP</span>}
        </div>
        <h3>{r.titulo}</h3>
        {r.descripcion && <p className="recurso-desc">{r.descripcion}</p>}
        {hasBloques ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }} data-testid={`recurso-bloques-${r.id}`}>
            {bloques.map((b, i) => <ContentBloque key={i} b={b} idx={`${r.id}-${i}`} />)}
          </div>
        ) : (
          <div className="recurso-actions">
            {r.tipo === "archivo" && r.file_url && (
              <a className="btn btn-gold btn-sm" href={fileUrl(r.file_url)} target="_blank" rel="noopener noreferrer"
                download={r.filename || true} data-testid={`recurso-descargar-${r.id}`}>
                <Download size={14} /> Descargar
              </a>
            )}
            {r.tipo === "enlace" && r.url && (
              <a className="btn btn-gold btn-sm" href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`recurso-abrir-${r.id}`}>
                Abrir recurso <ExternalLink size={14} />
              </a>
            )}
            {r.tipo === "imagen" && r.imagen && (
              <a className="btn btn-gold btn-sm" href={fileUrl(r.imagen)} target="_blank" rel="noopener noreferrer" data-testid={`recurso-verimg-${r.id}`}>
                Ver imagen <ExternalLink size={14} />
              </a>
            )}
            {r.tipo === "youtube" && r.url && (
              <a className="abtn" href={r.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                Ver en YouTube <ExternalLink size={13} />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ZonaRecursos() {
  const { data: recursos = [], isLoading } = useQuery({ queryKey: ["recursos"], queryFn: getRecursos });
  const [filtro, setFiltro] = useState("Todos");
  const [q, setQ] = useState("");
  const cats = useMemo(() => ["Todos", ...Array.from(new Set(recursos.map((r) => r.categoria).filter(Boolean)))], [recursos]);
  const term = q.trim().toLowerCase();
  const lista = recursos.filter((r) => {
    const okCat = filtro === "Todos" || r.categoria === filtro;
    const okQ = !term || `${r.titulo} ${r.descripcion || ""}`.toLowerCase().includes(term);
    return okCat && okQ;
  });

  return (
    <div data-testid="zona-recursos">
      <div className="main-head">
        <div>
          <h1>Zona de <span className="gold-it">recursos</span></h1>
          <p className="sub">Software de MAIA, prompts, plantillas y herramientas para crear agentes de IA. Cópialos, úsalos y véndelos.</p>
        </div>
      </div>

      {recursos.length > 0 && (
        <div className="recurso-toolbar">
          <div className="recurso-search">
            <Search size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar recurso por nombre…" data-testid="recurso-buscar" />
          </div>
          <div className="comu-tabs" style={{ marginBottom: 0 }}>
            {cats.map((c) => (
              <button key={c} data-testid={`recurso-filtro-${c}`} className={`comu-tab ${filtro === c ? "on" : ""}`} onClick={() => setFiltro(c)}>{c}</button>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <p style={{ color: "var(--muted)" }}>Cargando recursos…</p>
      ) : recursos.length === 0 ? (
        <div style={{ color: "var(--muted)", display: "flex", gap: 12, alignItems: "center" }} data-testid="recursos-vacio">
          <Package size={20} /> Aún no hay recursos publicados. Muy pronto subiremos el software y las plantillas de MAIA.
        </div>
      ) : lista.length === 0 ? (
        <p style={{ color: "var(--muted)" }} data-testid="recursos-sin-resultados">Ningún recurso coincide con tu búsqueda.</p>
      ) : (
        (() => {
          const grupos = {};
          lista.forEach((r) => { (grupos[r.categoria || "General"] ||= []).push(r); });
          return (
            <div data-testid="recurso-grid">
              {Object.entries(grupos).map(([sec, items]) => (
                <div key={sec} className="recurso-seccion">
                  <h3 className="recurso-seccion-tit" data-testid={`recurso-seccion-${sec}`}>{sec} <span>· {items.length}</span></h3>
                  <div className="recurso-grid-inner">
                    {items.map((r) => <RecursoCard key={r.id} r={r} />)}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      )}
    </div>
  );
}
